import * as THREE from 'three';
import { CONFIG } from '../config.js';

/**
 * @typedef {'default' | 'aim'} CameraMode
 */

/**
 * Third-person camera system with multiple modes and mouse orbit.
 * Follows the cat and avoids clipping through houses.
 */
export class CameraController {
    /** @type {THREE.PerspectiveCamera} */
    #camera;

    /** @type {CameraMode} */
    #mode = 'default';

    /** @type {number} */
    #orbitAngle = 0;

    /** @type {number} */
    #pitchAngle = 0;

    /** @type {boolean} */
    #isDragging = false;

    /** @type {{x: number, y: number}} */
    #previousMousePosition = { x: 0, y: 0 };

    /**
     * @param {HTMLCanvasElement} canvas - The renderer's canvas for mouse events
     */
    constructor(canvas) {
        this.#camera = new THREE.PerspectiveCamera(
            75,
            window.innerWidth / window.innerHeight,
            0.1,
            1000
        );

        this.#setupMouseControls(canvas);
        this.#setupWindowResize();
    }

    /**
     * Get the Three.js camera
     * @returns {THREE.PerspectiveCamera}
     */
    get camera() {
        return this.#camera;
    }

    /**
     * Get current camera mode
     * @returns {CameraMode}
     */
    get mode() {
        return this.#mode;
    }

    /**
     * Set camera mode
     * @param {CameraMode} mode
     */
    set mode(mode) {
        this.#mode = mode;
    }

    /**
     * Setup mouse drag controls for camera rotation
     * @param {HTMLCanvasElement} canvas
     */
    #setupMouseControls(canvas) {
        canvas.addEventListener('mousedown', (e) => {
            this.#isDragging = true;
            this.#previousMousePosition = { x: e.clientX, y: e.clientY };
        });

        canvas.addEventListener('mouseup', () => {
            this.#isDragging = false;
        });

        canvas.addEventListener('mouseleave', () => {
            this.#isDragging = false;
        });

        canvas.addEventListener('mousemove', (e) => {
            if (!this.#isDragging) return;

            const deltaMove = {
                x: e.clientX - this.#previousMousePosition.x,
                y: e.clientY - this.#previousMousePosition.y
            };

            // Horizontal rotation
            this.#orbitAngle -= deltaMove.x * 0.01;

            // Vertical rotation (pitch)
            this.#pitchAngle -= deltaMove.y * 0.01;
            this.#pitchAngle = Math.max(
                CONFIG.camera.minPitch,
                Math.min(CONFIG.camera.maxPitch, this.#pitchAngle)
            );

            this.#previousMousePosition = { x: e.clientX, y: e.clientY };
        });
    }

    /**
     * Setup window resize handler
     */
    #setupWindowResize() {
        window.addEventListener('resize', () => {
            this.#camera.aspect = window.innerWidth / window.innerHeight;
            this.#camera.updateProjectionMatrix();
        });
    }

    /**
     * Reset camera to default view
     */
    reset() {
        this.#mode = 'default';
        this.#orbitAngle = 0;
        this.#pitchAngle = 0;
    }

    /**
     * Update camera position to follow target
     * @param {THREE.Vector3} targetPosition - Position to follow (cat)
     * @param {number} targetRotation - Rotation of target
     * @param {any[]} houses - Houses to avoid clipping through
     */
    update(targetPosition, targetRotation, houses) {
        const catDirection = new THREE.Vector3(0, 0, 1); // Back is positive Z
        catDirection.applyAxisAngle(new THREE.Vector3(0, 1, 0), targetRotation);

        // Get current camera offset based on mode
        let currentCameraOffset;
        if (this.#mode === 'aim') {
            currentCameraOffset = CONFIG.camera.aimOffset;
        } else {
            currentCameraOffset = CONFIG.camera.defaultOffset;
        }

        // Calculate camera position
        const newCameraPosition = new THREE.Vector3();
        newCameraPosition.copy(targetPosition);

        if (this.#mode === 'aim') {
            // In aim mode, position camera in front and above the cat
            const frontDirection = new THREE.Vector3(0, 0, -1);
            frontDirection.applyAxisAngle(new THREE.Vector3(0, 1, 0), targetRotation);

            newCameraPosition.y += currentCameraOffset.y;
            frontDirection.multiplyScalar(Math.abs(currentCameraOffset.z));
            newCameraPosition.add(frontDirection);
        } else {
            // Default third-person camera with orbit
            const cameraDir = catDirection.clone();
            cameraDir.applyAxisAngle(new THREE.Vector3(0, 1, 0), this.#orbitAngle);

            // Apply vertical offset considering pitch
            newCameraPosition.y += currentCameraOffset.y * Math.cos(this.#pitchAngle);

            // Move camera back considering pitch
            const horizontalDistance = currentCameraOffset.z * Math.cos(this.#pitchAngle);
            const verticalOffset = currentCameraOffset.z * Math.sin(this.#pitchAngle);
            cameraDir.multiplyScalar(horizontalDistance);
            newCameraPosition.add(cameraDir);
            newCameraPosition.y += verticalOffset;
        }

        // Check for camera collisions with houses
        for (const house of houses) {
            const cameraToHouse = new THREE.Vector3().subVectors(newCameraPosition, house.position);
            const distance = cameraToHouse.length();

            if (distance < CONFIG.camera.houseCollisionDistance) {
                // Move camera position outward
                const pushDirection = cameraToHouse.normalize();
                newCameraPosition.copy(house.position).add(
                    pushDirection.multiplyScalar(CONFIG.camera.houseCollisionDistance)
                );

                // Maintain height
                if (this.#mode === 'aim') {
                    newCameraPosition.y = targetPosition.y + currentCameraOffset.y;
                } else {
                    newCameraPosition.y = targetPosition.y +
                        currentCameraOffset.y * Math.cos(this.#pitchAngle) +
                        currentCameraOffset.z * Math.sin(this.#pitchAngle);
                }
            }
        }

        // Smoothly move camera
        this.#camera.position.lerp(newCameraPosition, CONFIG.camera.lerpFactor);

        // Look at target
        let lookAtPosition;
        if (this.#mode === 'aim') {
            // In aim mode, look at a point behind the cat (where poop will land)
            const backDirection = new THREE.Vector3(0, 0, 1);
            backDirection.applyAxisAngle(new THREE.Vector3(0, 1, 0), targetRotation);
            lookAtPosition = targetPosition.clone().add(backDirection.multiplyScalar(1.5));
            lookAtPosition.y = 0.2; // Look slightly above ground
        } else {
            // Regular mode - look at the cat
            lookAtPosition = targetPosition.clone();
            lookAtPosition.y += 0.5;
        }

        this.#camera.lookAt(lookAtPosition);
    }
}
